import dayjs from "dayjs";
import { useState } from "react";
import {
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { CustomRechartsDot } from "../CustomRechartsDot/CustomRechartsDot";
import { DualAxisChartLegend } from "./DualAxisChartModal";
import {
  DualAxisDataPoint,
  DualAxisSeries,
  MOCK_CFDNA_DATA,
  MOCK_FEV1_DATA,
  MOCK_SERIES,
} from "./mockData";

type DualAxisChartProps = {
  variant: "card" | "modal";
  series?: DualAxisSeries[];
  dataSets?: DualAxisDataPoint[][];
  onPointClick?: (point: DualAxisDataPoint | null) => void;
};

function ClickableDot(props: any) {
  const { cx, cy, fill, onDotClick, payload } = props;
  const [hover, setHover] = useState(false);
  if (cx == null || cy == null) return null;

  return (
    <g
      style={{ cursor: "pointer" }}
      onClick={() => onDotClick?.(payload)}
      onTouchStart={() => onDotClick?.(payload)}
      onMouseOver={() => setHover(true)}
      onMouseOut={() => setHover(false)}
    >
      <circle cx={cx} cy={cy} r={hover ? 4.5 : 3} fill={fill} />
      {hover && (
        <circle
          cx={cx}
          cy={cy}
          r={7}
          fill="none"
          stroke={fill}
          strokeWidth={2}
          strokeOpacity={0.4}
        />
      )}
    </g>
  );
}

function DualAxisTooltip({ active, payload }: any) {
  if (!active || !payload?.length) return null;

  const time = payload[0]?.payload?.time;

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #ddd",
        borderRadius: 4,
        padding: "8px 12px",
        fontSize: 12,
        fontFamily: "Open Sans, sans-serif",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      }}
    >
      {time && (
        <div style={{ color: "#666", marginBottom: 4, fontWeight: 600 }}>
          {time}
        </div>
      )}
      {payload
        .filter((entry: any) => entry.dataKey !== "unformattedTime")
        .map((entry: any) => (
          <div key={entry.dataKey} style={{ color: entry.fill }}>
            {entry.name ?? entry.dataKey}: {entry.value}
          </div>
        ))}
    </div>
  );
}

export function DualAxisChart({
  variant,
  series = MOCK_SERIES,
  dataSets = [MOCK_FEV1_DATA, MOCK_CFDNA_DATA],
  onPointClick,
}: DualAxisChartProps) {
  const isCard = variant === "card";

  const leftSeries = series.filter((s) => s.yAxisId === "left");
  const rightSeries = series.filter((s) => s.yAxisId === "right");

  const leftUnit = leftSeries[0]?.unit ?? "";
  const rightUnit = rightSeries[0]?.unit ?? "";

  const xDomain = [
    dayjs().subtract(10, "month").startOf("month").valueOf(),
    dayjs().add(7, "day").valueOf(),
  ];

  const tickMonths = Array.from({ length: 11 }, (_, i) =>
    dayjs()
      .subtract(10 - i, "month")
      .startOf("month")
      .valueOf()
  );

  return (
    <ResponsiveContainer
      width={isCard ? "100%" : 900}
      height={isCard ? 220 : "78%"}
    >
      <ScatterChart
        margin={
          isCard
            ? { top: 8, right: 8, bottom: 0, left: 0 }
            : { top: 24, right: 20, bottom: 0, left: 0 }
        }
      >
        <CartesianGrid strokeDasharray="2" vertical={false} />

        {/* Left Y-axis */}
        <YAxis
          yAxisId="left"
          orientation="left"
          width={isCard ? 30 : 48}
          tickMargin={8}
          stroke="#D8D8D8"
          domain={["auto", "auto"]}
          label={
            !isCard
              ? {
                  value: leftUnit,
                  position: "top",
                  offset: 12,
                  style: {
                    fontSize: 12,
                    fill: "#727272",
                    textAnchor: "middle",
                  },
                }
              : undefined
          }
          tick={{
            fill: "#727272",
            fontSize: isCard ? 10 : 12,
            fontWeight: 400,
          }}
          hide={isCard}
        />

        {/* Right Y-axis */}
        <YAxis
          yAxisId="right"
          orientation="right"
          width={isCard ? 30 : 48}
          tickMargin={8}
          stroke="#D8D8D8"
          domain={[0, 2]}
          label={
            !isCard
              ? {
                  value: rightUnit,
                  position: "top",
                  offset: 12,
                  style: {
                    fontSize: 12,
                    fill: "#727272",
                    textAnchor: "middle",
                  },
                }
              : undefined
          }
          tick={{
            fill: "#727272",
            fontSize: isCard ? 10 : 12,
            fontWeight: 400,
          }}
          hide={isCard}
        />

        <XAxis
          dataKey="unformattedTime"
          type="number"
          domain={xDomain}
          ticks={isCard ? undefined : tickMonths}
          tickFormatter={(v) => dayjs(v).format("MMM")}
          tickMargin={isCard ? 6 : 14}
          stroke="#D8D8D8"
          minTickGap={isCard ? 40 : 60}
          interval={0}
          hide={isCard}
          tick={{
            fill: "#727272",
            fontSize: isCard ? 10 : 12,
            fontWeight: 400,
          }}
        />

        {!isCard && (
          <Tooltip
            cursor={false}
            content={<DualAxisTooltip />}
            wrapperStyle={{ outline: "none" }}
          />
        )}

        {!isCard && (
          <Legend
            content={
              <DualAxisChartLegend series={series} dataSets={dataSets} />
            }
          />
        )}

        {series.map((s, idx) => {
          const isClickable = !!onPointClick && s.yAxisId === "left";
          return (
            <Scatter
              key={s.yKey}
              yAxisId={s.yAxisId}
              dataKey={s.yKey}
              data={dataSets[idx] ?? []}
              fill={s.color}
              isAnimationActive={false}
              line={{
                stroke: s.color,
                strokeWidth: 1.4,
                opacity: s.lineOpacity ?? 1,
                strokeDasharray: s.strokeDasharray,
              }}
              lineJointType={s.lineJointType ?? "monotoneX"}
              shape={
                isClickable ? (
                  <ClickableDot
                    fill={s.color}
                    onDotClick={(p: any) => onPointClick(p)}
                  />
                ) : (
                  <CustomRechartsDot fill={s.color} shape={s.dotShape} />
                )
              }
            />
          );
        })}
      </ScatterChart>
    </ResponsiveContainer>
  );
}
